<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef, watch } from "vue";
import ArrowLeft from "@lucide/vue/dist/esm/icons/arrow-left.mjs";
import ArrowRight from "@lucide/vue/dist/esm/icons/arrow-right.mjs";
import Play from "@lucide/vue/dist/esm/icons/play.mjs";
import Square from "@lucide/vue/dist/esm/icons/square.mjs";
import RotateCcw from "@lucide/vue/dist/esm/icons/rotate-ccw.mjs";
import Loader from "@lucide/vue/dist/esm/icons/loader.mjs";
import Check from "@lucide/vue/dist/esm/icons/check.mjs";
import {
    GlassDock,
    DockIconButton,
    DockTabButton,
    DockLayerGroup,
    DockLayer,
} from "@mkbabb/glass-ui/dock";
import { IconTooltip } from "@mkbabb/glass-ui/icon-tooltip";
import AppSettingsButton from "@src/components/AppSettingsButton.vue";
import {
    METRIC_DEFINITIONS,
    isMetricId,
    phaseFor,
} from "@src/types/speedtest.ts";
import { getLucideIcon } from "@src/utils/icons.ts";
import { NumericAnimation } from "@mkbabb/keyframes.js";
import { easeOutCubic } from "@design";
import { useRingPulseClock } from "@features/speedtest/composables/meter/useRingPulseClock.ts";

/**
 * Dock UI status surface — the three speedtest signals the dock binds to.
 * Inlined here so the dock owns its own contract.
 */
export interface SpeedtestStatus {
    isRunning: boolean;
    isCompleted: boolean;
    currentPhase?: string;
}

export type AppView = "speedtest" | "survey" | "dashboard" | "thankyou";

export interface SurveyDockState {
    isFirstStep: boolean;
    isLastStep: boolean;
    editingFromReview: boolean;
    /** True when the current survey step passes validation — the user may
     *  advance. When true the Next CTA renders `data-tier="primary"` +
     *  `btn-audacious-gold` (gold hover shimmer). When false it renders
     *  quiet (muted-foreground, no glass tile). */
    canAdvance: boolean;
}

const props = defineProps<{
    currentView: AppView;
    speedtestStatus: SpeedtestStatus;
    canGoBack: boolean;
    surveyState?: SurveyDockState;
    testOnly?: boolean;
}>();

const isRunning = computed(() => props.speedtestStatus.isRunning);
const testCompleted = computed(() => props.speedtestStatus.isCompleted);

// Phase icon resolves through METRIC_DEFINITIONS + getLucideIcon. The
// `"started"` branch is preserved because it's a transient run-but-
// pre-phase signal the engine emits before the first `ping` tick —
// it's NOT a MetricId, so it falls outside the per-metric icon
// vocabulary and stays mapped to `Loader` inline. Jitter aliases
// through `phaseFor` when the engine emits it (currently jitter rides
// ping, so this is defensive).
const currentPhaseIcon = computed(() => {
    const phase = props.speedtestStatus.currentPhase;
    if (!phase) return null;
    if (phase === "started") return Loader;
    if (isMetricId(phase)) {
        return getLucideIcon(METRIC_DEFINITIONS[phase].icon);
    }
    // Engine `Phase` (ping | download | upload) is a strict subset of
    // MetricId, so the guard above already covers it. Defensive fallback:
    // route any non-canonical string through the phaseFor mapping in case
    // an upstream emits a latency-family alias.
    if (phase === "jitter") {
        return getLucideIcon(METRIC_DEFINITIONS[phaseFor("jitter")].icon);
    }
    return null;
});

const isSpinningPhase = computed(
    () => props.speedtestStatus.currentPhase === "started",
);

const emit = defineEmits<{
    start: [];
    stop: [];
    next: [];
    retake: [];
    back: [];
    forward: [];
}>();

const dockRef = useTemplateRef<InstanceType<typeof GlassDock>>("dockRef");

const isSurvey = computed(
    () => props.currentView === "survey" && !!props.surveyState,
);
const isSurveyView = computed(() => props.currentView === "survey");

// ── Survey primary button ──────────────────────────────────────────────

const surveyLabel = computed(() => {
    if (isSurvey.value) {
        const s = props.surveyState!;
        if (s.editingFromReview) return "Done";
        return s.isLastStep ? "Submit" : "Next";
    }
    if (isSurveyView.value) return "Next";
    return null;
});

const surveyIcon = computed(() => {
    if (isSurvey.value) {
        const s = props.surveyState!;
        if (s.isLastStep || s.editingFromReview) return Check;
    }
    return ArrowRight;
});

// ── Active dock layer (derived from app state) ─────────────────────────
// Each discrete middle-of-dock composition is a named layer. DockLayerGroup
// crossfades and FLIP-animates width when the active layer changes,
// replacing the previous v-if pop-in pattern.
type DockLayerId =
    | "speedtest-idle"
    | "speedtest-running"
    | "speedtest-complete"
    | "survey"
    | "thankyou";

const activeLayer = computed<DockLayerId>({
    get() {
        if (props.currentView === "survey") return "survey";
        if (props.currentView === "thankyou") return "thankyou";
        // Speedtest + dashboard views share the three sub-states.
        if (isRunning.value) return "speedtest-running";
        if (testCompleted.value) return "speedtest-complete";
        return "speedtest-idle";
    },
    // DockLayerGroup requires a writable v-model. Layer switching is
    // state-driven; this setter is a no-op because `showRail=false`
    // suppresses user-initiated switching.
    set() {},
});

// ── Dock signature pulse (B5) — AV.W14.2 ───────────────────────────────
//
// The dock is the SECOND channel of the ONE jittered ring-pulse clock: it
// pulses in lockstep with the dial (B0) off the SAME `armNonce`, so the
// at-rest instrument acknowledges itself across both loci on one clock — no
// second scheduler in the dock, no prop-drill (feedback_clock_owner_pattern
// / DDR-AV-W14-BREATH). The clock owns the schedule + the PRM-park + the
// rising-edge reset; the dock only WATCHES the arm and plays a one-shot
// signature swell.
//
// The swell ENVELOPE is a keyframes.js `NumericAnimation` (0→1→0,
// `easeOutCubic`) — the SAME engine + curve the canvas ring-pulse rides, so
// the two loci read as one vocabulary (NOT a separate CSS `@keyframes`,
// which would also split the central keyframe registry the C4-F1 doctrine
// owns). The per-frame callback writes `--dock-pulse` (a [0,1] intensity)
// onto the dock host; the scoped style maps it to a restrained edge-light
// glow swell in the ring-0 canon hue (`#cc2233` — the SAME hue the canvas
// dial pulse uses at idle, so dial + dock read as one signature; the pulse
// only ever fires at idle, so the at-rest hue is the right register).
// `respectReducedMotion` snaps the envelope to its `{ p: 0 }` end-state, so
// under reduced-motion no swell paints — matching the clock's own PRM-no-arm.
const pulseClock = useRingPulseClock();
const dockPulse = ref(0);
let signatureAnim: NumericAnimation<{ p: number }> | null = null;

watch(pulseClock.armNonce, () => {
    signatureAnim?.stop();
    const anim = new NumericAnimation<{ p: number }>(
        [{ p: 0 }, { p: 1 }, { p: 0 }],
        {
            duration: 380,
            timingFunction: easeOutCubic,
            respectReducedMotion: true,
        },
    );
    signatureAnim = anim;
    void anim
        .play(({ p }) => {
            dockPulse.value = p;
        })
        .then(() => {
            if (signatureAnim === anim) {
                dockPulse.value = 0;
                signatureAnim = null;
            }
        });
});

onUnmounted(() => signatureAnim?.stop());
</script>

<template>
    <!-- Inline-flow host: renders at the bottom of App.vue's main column.
         Breathing-room is symmetric — `py-(--card-edge-inset)` gives the
         floating pill equal top + bottom inset off the surrounding flow.
         Pre-fix the host carried `pb-(--dock-inset)` only: an asymmetric
         bottom-heavy gap (the `--dock-inset` ladder reaches `clamp(4rem,
         12dvh, 9rem)` on tall desktop) with no matching top inset, which
         read as a deep dead pasture below the dock. `--card-edge-inset`
         is the canonical single-rhythm breathing token (`clamp(0.5rem,
         1.5vh, 1rem)`) — modest, viewport-tracked, and identical on both
         axes so the pill sits balanced rather than shoved upward.
         `flex-shrink-0` keeps the dock height fixed when the route slot
         consumes the available column space. -->
    <div
        class="z-dock pointer-events-none flex flex-shrink-0 items-center
            justify-center py-(--card-edge-inset)"
        :style="{ 'min-block-size': 'var(--dock-host-reserve)' }"
    >
        <div
            class="dock-in dock-signature pointer-events-auto"
            :style="{ '--dock-pulse': dockPulse }"
        >
            <GlassDock
                ref="dockRef"
                :collapse-delay="3000"
                :start-collapsed="false"
                position="inline"
                :always-expanded="true"
                density="audacious"
                shape="pill"
            >
                <!-- Back button — `v-if="canGoBack"` retires the disabled
                     state at idle so the idle dock reads as `Start | gear`
                     (the disabled-back-button paint at idle was reading as
                     "loading" rather than "unavailable"; per F2.I-03 the
                     cleaner affordance is to not paint it at all). -->
                <IconTooltip v-if="canGoBack" text="Back">
                    <DockIconButton aria-label="Back" @click="emit('back')">
                        <ArrowLeft aria-hidden="true" />
                    </DockIconButton>
                </IconTooltip>

                <div v-if="canGoBack" class="dock-separator" />

                <!-- ═══ CONTEXTUAL LAYER GROUP ═══
                     Middle dock region swaps between named layers driven by
                     app state. DockLayerGroup handles crossfade + FLIP width
                     animation; showRail=false because selection is derived,
                     not user-controlled. -->
                <DockLayerGroup v-model:active="activeLayer" :show-rail="false">
                    <DockLayer id="speedtest-idle">
                        <!-- Labelled DockTabButton: the visible `.dock-label`
                             IS the affordance. IconTooltip is reserved for
                             icon-only buttons (DockIconButton). Wrapping a
                             labelled button doubles the announcement on
                             screen readers + paints a tooltip pill that
                             duplicates the on-button text. -->
                        <!-- AS-8/10/16/X3 — the idle Start is the PRIMARY
                             control, but absent a tier DockTabButton rests at
                             `--muted-foreground` (witnessed rgb(163,161,153),
                             dataTier:null) = the disabled register the user
                             read as greyed-out. `data-tier="primary"` makes
                             DockTabButton paint the computed `--foreground` +
                             composed-glass tile, matching the survey/thankyou
                             primary CTAs (same idiom as the `data-tier` set on
                             the survey Next + the thankyou New Test below). -->
                        <DockTabButton
                            class="gap-[var(--dock-layer-gap)]"
                            data-tier="primary"
                            @click="emit('start')"
                        >
                            <Play
                                class="dock-glyph relative"
                                aria-hidden="true"
                            />
                            <span class="dock-label relative">Start</span>
                        </DockTabButton>
                    </DockLayer>

                    <DockLayer id="speedtest-running">
                        <!-- Next sits alongside Stop while the test runs.
                             The engine submits partial results via
                             `submitPhaseSnapshot()` at every phase
                             boundary, and the Pinia store is app-scoped,
                             so a mid-test Next navigation to /survey
                             doesn't abandon the run. Next is the primary
                             path forward (DockTabButton with label);
                             Stop is the secondary abort (DockIconButton). -->
                        <IconTooltip text="Stop">
                            <DockIconButton
                                aria-label="Stop"
                                @click="emit('stop')"
                            >
                                <Square aria-hidden="true" />
                            </DockIconButton>
                        </IconTooltip>
                        <!-- AS-16/X3 — Next is the primary forward path
                             (the survey hand-off). Without a tier it rested
                             muted like the idle Start; `data-tier="primary"`
                             paints computed `--foreground` so the forward CTA
                             reads as the live affordance, not disabled chrome. -->
                        <DockTabButton
                            v-if="!testOnly"
                            class="gap-[var(--dock-layer-gap)]"
                            data-tier="primary"
                            @click="emit('next')"
                        >
                            <ArrowRight class="dock-glyph" aria-hidden="true" />
                            <span class="dock-label">Next</span>
                        </DockTabButton>
                    </DockLayer>

                    <DockLayer id="speedtest-complete">
                        <IconTooltip text="Retake">
                            <DockIconButton
                                aria-label="Retake"
                                @click="emit('retake')"
                            >
                                <RotateCcw aria-hidden="true" />
                            </DockIconButton>
                        </IconTooltip>
                        <!-- AS-16/X3 — Next is the primary forward path at
                             complete too; `data-tier="primary"` matches the
                             running-layer Next so the forward CTA paints
                             computed `--foreground`, not the muted register. -->
                        <DockTabButton
                            v-if="!testOnly"
                            class="gap-[var(--dock-layer-gap)]"
                            data-tier="primary"
                            @click="emit('next')"
                        >
                            <ArrowRight class="dock-glyph" aria-hidden="true" />
                            <span class="dock-label">Next</span>
                        </DockTabButton>
                    </DockLayer>

                    <DockLayer id="survey">
                        <!-- D9 — Next CTA tier + gold shimmer.
                             When `canAdvance` is true the step passes
                             validation and progression is live — the
                             button receives `data-tier="primary"` (which
                             DockTabButton translates to `btn-audacious`:
                             foreground text, composed-glass tile, edge-
                             light). `btn-audacious-gold` composes the
                             gold sweep shimmer on hover (one-pass, hover-
                             gated celebration, not resting chrome).
                             When `!canAdvance` the tier and gold class
                             are absent — the button paints quiet
                             `--muted-foreground`, transparent bg: honest
                             "not yet" rather than a disabled affordance.
                             The `disabled` attribute is NOT set because
                             the dock's forward handler validates before
                             advancing; we express the state through
                             visual register, not interactivity block. -->
                        <DockTabButton
                            class="gap-[var(--dock-layer-gap)]"
                            :class="{
                                'btn-audacious-gold': surveyState?.canAdvance,
                            }"
                            :data-tier="
                                surveyState?.canAdvance ? 'primary' : undefined
                            "
                            @click="emit('forward')"
                        >
                            <component
                                :is="surveyIcon"
                                class="dock-glyph"
                                aria-hidden="true"
                            />
                            <span class="dock-label">{{ surveyLabel }}</span>
                        </DockTabButton>
                    </DockLayer>

                    <DockLayer id="thankyou">
                        <DockTabButton
                            class="gap-[var(--dock-layer-gap)]"
                            data-tier="primary"
                            @click="emit('forward')"
                        >
                            <RotateCcw class="dock-glyph" aria-hidden="true" />
                            <span class="dock-label">New Test</span>
                        </DockTabButton>
                    </DockLayer>
                </DockLayerGroup>

                <!-- Settings shelf — right of the contextual layer group,
                     separated by the canonical `.dock-separator` divider.
                     The popover anchors `side="top"` so content opens up
                     into the chassis rather than off-screen below the dock. -->
                <div class="dock-separator" />
                <!-- DDR-AV-PAUSE / D5 (W14) — the `DockBackgroundToggle`
                     pause/play control is REMOVED (REVERSES `3be10905`). The
                     Aurora is decorative ambient motion already parked by
                     `prefers-reduced-motion`, so the visible toggle was
                     confusing chrome; the PRM park is the conformant
                     WCAG-2.2.2 path. See docs/decisions/DDR-AV-W14-B0-PULSE.md
                     §Pause-removal. -->
                <AppSettingsButton />

                <template #collapsed>
                    <!-- Phase-icon swap: the `fade` transition (180ms
                         opacity, no transform) reads as continuity
                         rather than a hard cut. `mode="out-in"` keeps
                         only one icon paint at a time. `currentPhaseIcon`
                         resolves through METRIC_DEFINITIONS[phase].icon
                         → getLucideIcon and falls back to `Loader` for
                         the transient `"started"` state (not a MetricId). -->
                    <Transition name="fade" mode="out-in">
                        <component
                            v-if="isRunning && currentPhaseIcon"
                            :is="currentPhaseIcon"
                            :key="`phase-${speedtestStatus.currentPhase}`"
                            class="dock-glyph text-th-accent-opaque shrink-0"
                            :class="{ 'animate-spin': isSpinningPhase }"
                            aria-hidden="true"
                        />
                        <Play
                            v-else
                            key="phase-idle"
                            class="dock-glyph shrink-0"
                            aria-hidden="true"
                        />
                    </Transition>
                    <!-- `.dock-label` (weight 500) keeps the collapsed
                         header at the same weight register as the
                         expanded DockTabButton labels. -->
                    <span class="dock-label whitespace-nowrap">
                        {{ testCompleted ? "Complete" : "Speedtest" }}
                    </span>
                </template>
            </GlassDock>
        </div>
    </div>
</template>

<style scoped>
/* DIAL-CLS-CI (deploy-gate-repair) — size-stable dock host reserve.
   The `.z-dock` host renders as the SECOND child of App.vue's `<main>` flex
   column (sibling to the `flex-1` instrument chassis). It is `flex-shrink-0`
   + `items-center`, so its block-size is driven by the GlassDock pill it
   centers — and that pill is NOT frame-0-stable: on hydration the
   `DockLayerGroup` resolves its active layer (button count 8→9, witnessed),
   which shrinks the audacious pill ~146px→129px once. Because the host
   tracked the pill (`min-block-size:auto`), that 17px shrink propagated to
   the column: the host moved DOWN ~17px and the sibling chassis/`speedtest-
   column` grew ~8px to refill — the residual deploy-gate dial-CLS source
   (LayoutShift attribution: `div.speedtest-column` 639→647 + `div.z-dock`
   y673→690, the dominant 0.171 co-shift that survived the dial-cell reserve).

   The dock-scale is byte-stable from frame 0 (`--dock-scale: calc(1.5*1.2)`,
   witnessed) — this is a library-internal active-layer content settle, not a
   consumer geometry bug. The idiomatic consumer fix is to DECOUPLE the
   column from the pill's internal settle by reserving the host's block-size
   at the pill's first-paint peak: the host then stays a fixed box and the
   pill centers WITHIN it (the `items-center` already does this) without ever
   moving the column. `--dock-host-reserve` is the audacious pill's peak
   content (`--dock-control-size` rung + the labelled-tab layer + the
   `--dock-layer-gap` rhythm) plus the host's own `py-(--card-edge-inset)`
   doubled — all published dock tokens, NOT a magic literal — so the reserve
   tracks the dock's own scale ladder. NO product-visible change: the pill's
   resting appearance is identical; this only pins the host's outer box so
   the pill's one-time layer settle reflows nothing below. Scoped to this
   consumer host. */
.z-dock {
    /* Peak audacious-pill content (control row + labelled tab + gap) +
       the host's symmetric `py-(--card-edge-inset)` (×2). The `max(…)`
       floor mirrors the library's WCAG 2.5.5 touch-target clamp so the
       reserve can never drop below a usable dock even at scale < 1. */
    --dock-host-reserve: calc(
        max(
            calc(
                var(--dock-control-size, 4rem) +
                    var(--dock-tab-min-height, 4rem) +
                    var(--dock-layer-gap, 0.5rem)
            ),
            9.125rem
        ) + var(--card-edge-inset) * 2
    );
}

/* ── Cockpit presence (I-2) — AV.W22.2 ─────────────────────────────────
   The dock is THE primary control surface, so it rides glass-ui's
   `density="audacious"` rung (the 4rem control box) and a viewport-keyed
   `--dock-local-scale` ladder so the cockpit reads bolder as the viewport
   earns it. `--dock-local-scale` is the dock-local EXTRA multiplier the
   library threads on TOP of the global `--ui-scale` comfort axis
   (`--dock-scale = --ui-scale × --dock-local-scale`, glass-ui/tokens.css):
   one knob is meant to grow box+pad+gap+tab+tile+glyph in lockstep through the
   existing `calc(<base> * --dock-scale)` cascade — zero per-axis edit, zero
   hand-rolled geometry.

   ── ASK-GU-DOCKSCALE re-home (inv-16; routed in coordination/ADOPTION-ASKS) ──
   The `--dock-local-scale` lever does NOT thread as documented at 3.10.0: both
   `--dock-scale` and `--dock-icon-glyph` are DECLARED only on `:root`
   (tokens.css), and neither is `@property`-registered, so their `calc()` is
   substituted ONCE at `:root` against `:root`'s `--dock-local-scale` (the
   identity 1). A consumer override of `--dock-local-scale` on a dock scope is
   inherited but never re-evaluated — the dock keeps `--dock-scale: calc(1 * 1)`
   and the box/glyph stay at the unscaled audacious base (witnessed:
   `audit/w22-witness.md`). The library's OWN coarse-pointer lift works only
   because it moves `--ui-scale` at `:root` (where the calc lives), not
   `--dock-local-scale` on the dock.

   The conformant consumer move is to RE-HOME the SAME two published formulas on
   the `.dock-signature` wrapper (the ANCESTOR of `.glass-dock`) so the `calc()`
   re-substitutes against THIS scope's `--dock-local-scale` and then inherits the
   resolved scalar down to the dock — the custom-prop-on-consumer-ancestor
   cascade pattern (AC.W6d; precept-6 keeps this `:deep`-free, the formula lands
   on the consumer host and the library reads it via natural inheritance). This
   is NOT hand-rolled geometry — it is the library's exact expression
   re-evaluated where its input is overridden; every magnitude still flows
   through the `calc(<base> * --dock-scale)` cascade. ASK-GU-DOCKSCALE asks
   glass-ui to re-home these onto `.glass-dock` (or `@property`-register
   `--dock-scale`); this re-home retires at R-CONSUME on publish.

   Because the re-home lives on `.dock-signature`, the COARSE-pointer mobile
   stack must also be owned here: the library's coarse block sets
   `--dock-local-scale: var(--dock-mobile-scale)` on `.glass-dock` (a DESCENDANT
   of `.dock-signature`), which our ancestor-scope `--dock-scale` cannot see — so
   the coarse block below mirrors that lift ON `.dock-signature` itself, and the
   re-homed scalar re-evaluates to `--ui-scale × --dock-mobile-scale`
   (witnessed 1.5 × 1.2 at touch). The WCAG 2.5.5 `max(…, --dock-touch-target)`
   floor clamp survives inside the library's control-size calc (the audacious
   4rem box already clears it; the coarse block also pins `--dock-control-floor`).

   Rungs (mirroring the project's 720/1280 breakpoints):
   - <720 fine — 1.0 (the audacious 4rem box is already bold on a narrow
     desktop window; no extra inflation, the shrink-wrapped pill must stay
     under the host width).
   - 720–1279 fine (laptop) — 1.15.
   - ≥1280 fine (desktop) — 1.25 (the bold cockpit register).
   - coarse pointer — `--ui-scale (1.5) × --dock-mobile-scale (1.2)` ≈ 1.8.
   The dock shrink-wraps its content, so the 1.25× pill stays well under the
   53rem (~848px) card ceiling at every rung (witnessed). */
.dock-signature {
    --dock-mobile-scale: 1.2;
    /* ASK-GU-DOCKSCALE interim re-home — the library's `:root` formulas, lifted
       onto the consumer ancestor so `--dock-local-scale` re-evaluates them and
       the resolved scalar inherits down to `.glass-dock`. Identical expressions
       to glass-ui/tokens.css §10; precept-6-compliant (no `:deep`); retire on
       R-CONSUME. */
    --dock-scale: calc(var(--ui-scale, 1) * var(--dock-local-scale, 1));
    --dock-icon-glyph: calc(1.25rem * var(--dock-scale));
}

@media (pointer: fine) and (min-width: 720px) {
    .dock-signature {
        --dock-local-scale: 1.15;
    }
}

/* Coarse pointer — own the mobile-scale lift on `.dock-signature` (mirroring
   the library's `.glass-dock[data-density]` coarse block, which our
   ancestor-scope re-home cannot reach) so the re-homed `--dock-scale` re-resolves
   to `--ui-scale × --dock-mobile-scale`. `--dock-control-floor` is pinned to the
   WCAG 2.5.5 touch target so the control-size `max(…)` clamp holds even if a
   future `--dock-mobile-scale` drops below 1. */
@media (pointer: coarse) {
    .dock-signature {
        --dock-local-scale: var(--dock-mobile-scale, 1);
        --dock-control-floor: var(--dock-touch-target, 2.75rem);
    }
}

@media (pointer: fine) and (min-width: 1280px) {
    .dock-signature {
        --dock-local-scale: 1.25;
    }
}

/* Glyph cascade — DockTabButton + collapsed-slot glyphs ride
   `--dock-icon-glyph` (the `--dock-scale`-keyed library token) so they grow
   WITH the box, the same as the DockIconButton glyphs (which the library
   sizes via `.dock-icon-button > svg { width: var(--dock-icon-glyph) }`).
   The library has NO `> svg` rule scoped to `.dock-tab-button`, so a bare
   lucide glyph inside a tab button falls to its intrinsic 24px and does NOT
   ride the scale cascade — dropping the hardcoded `size-icon-md/lg` alone
   would strand those glyphs at a fixed rem. This consumer rule reads the SAME
   `--dock-icon-glyph` token the library exposes for exactly this (NOT a
   reimplementation — it consumes the published lever), so every dock glyph —
   icon-button, tab-button, collapsed — rides ONE scale knob. The lucide
   component renders an `<svg>` root and spreads the consumer `class` onto it,
   so `.dock-glyph` matches the SVG element directly. */
.dock-glyph {
    width: var(--dock-icon-glyph, 1.25rem);
    height: var(--dock-icon-glyph, 1.25rem);
}

/* ── Dock signature pulse (B5) — AV.W14.2 ──────────────────────────────
   The dock's at-rest "alive" beat, painted off the `--dock-pulse` [0,1]
   intensity the keyframes.js envelope writes (see `<script setup>`). The
   swell is a restrained outer edge-light glow carrying the active phase hue
   — `--phase-color` when the chassis cascade reaches the dock, else the
   ring-0 canon `#cc2233` (the SAME hue the canvas dial pulse uses at idle,
   so the two loci read as one signature). DDR-AV-W14-BREATH.

   The swell is `filter: drop-shadow` + `box-shadow` ONLY — NOT `transform`.
   The dock host already carries a `dock-in` ENTRANCE animation that owns the
   `transform` property via `animation-fill-mode: both` (it holds its
   end-state permanently), so a `transform: scale()` pulse here would be
   shadowed by the held entrance transform and never paint (witnessed). The
   glow is the conflict-free, more tasteful edge-light register anyway — and
   `filter`/`box-shadow` are compositor-friendly, so the swell reflows
   nothing. The spread + hue both scale with `--dock-pulse`, so at rest
   (`0`) the dock is byte-identical to its prior paint — the pulse adds zero
   resting chrome, only the brief swell. */
.dock-signature {
    --dock-pulse: 0;
    /* The ring-0 canon `#cc2233` (reference_phase_palette_canon) — the SAME
       hue the canvas dial pulse uses at idle, so dial + dock read as ONE
       signature, never grey (DDR-AV-W14-BREATH). The pulse ONLY fires at
       idle (the clock withholds arms while running), so this is always the
       at-rest hue — NOT `var(--phase-color)`, which the chassis cascade
       resolves to the muted idle grey here (witnessed), exactly the grey the
       doctrine forbids. */
    --dock-pulse-hue: #cc2233;
    filter: drop-shadow(
        0 0 calc(14px * var(--dock-pulse))
            color-mix(
                in srgb,
                var(--dock-pulse-hue) calc(70% * var(--dock-pulse)),
                transparent
            )
    );
    box-shadow: 0 0 calc(20px * var(--dock-pulse))
        color-mix(
            in srgb,
            var(--dock-pulse-hue) calc(40% * var(--dock-pulse)),
            transparent
        );
}

/* PRM park — belt-and-suspenders. The clock already withholds arms under
   reduced-motion (no envelope runs, `--dock-pulse` stays 0), and the
   envelope's own `respectReducedMotion` snaps it to 0; this pins the swell
   to nothing structurally so even a stray non-zero `--dock-pulse` paints no
   glow under a reduced-motion preference. */
@media (prefers-reduced-motion: reduce) {
    .dock-signature {
        filter: none;
        box-shadow: none;
    }
}
</style>
